import {
  FC,
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import { publicIpv4 } from 'public-ip';
import {
  SorensonAccountProvider,
  LogInCredentials,
  Core,
  UserLoginStatus,
} from '@sorenson-eng/videophone-core-services';

import {
  setLocalItem,
  getLocalValue,
  removeLocalItem,
} from 'utils/services/storage';
import useAxios from 'utils/hooks/useAxios';
import libraryAxios, { AxiosInstance } from 'axios';
import ZoomType from 'utils/constants/zoomType';
import { useAuth0 } from '@auth0/auth0-react';
import UserType from '../constants/userType';
import { LoginModes } from 'utils/constants/loginModes';
import { LOCATION } from 'utils/constants';
interface IUserZoomData {
  canRequest: boolean;
  email: string;
  id: string;
  firstName: string;
  lastName: string;
  accountId: string;
  hearingPhoneNumber: string;
}
interface ISorensonUser {
  name: string;
  phoneNumber?: number;
  email?: string;
  userType: UserType;
}

interface IAuthContext {
  sorensonUser: ISorensonUser;
  userZoomData: IUserZoomData;
  accessToken: string;
  isLoggedIn: boolean;
  ip: string;
  location: string | null;
  setUserData: (name: string, phoneNumber: number, userType: UserType) => void;
  clearUserData: () => void;
  signIn: (data: LogInCredentials) => void;
  signOut: () => void;
  auth0Login: () => void;
  getUserZoomData: () => void;
  setAccessToken: (token: string) => void;
  setLocation: (state: string) => void;
  getTokenSilently: () => Promise<string>;
  loading: boolean;
  axios: AxiosInstance;
}

const sorensonUserInitial: ISorensonUser = {
  name: '',
  phoneNumber: 0,
  userType: UserType.BLANK,
};

const zoomInitial: IUserZoomData = {
  email: '',
  id: '',
  firstName: '',
  lastName: '',
  accountId: '',
  hearingPhoneNumber: '',
  canRequest: false,
};

export const AuthContext = createContext<IAuthContext>({
  sorensonUser: sorensonUserInitial,
  userZoomData: zoomInitial,
  accessToken: '',
  isLoggedIn: false,
  ip: '',
  location: '',
  setAccessToken: (token: string) => {
    console.log('Should not be executed ever', token);
  },
  setUserData: (userName: string, userPhoneNumber: number) => {
    console.log(userName, userPhoneNumber);
  },
  clearUserData: () => {},
  signIn: () => {},
  signOut: () => {},
  auth0Login: () => {},
  getUserZoomData: () => {},
  setLocation: () => {},
  getTokenSilently: async () => {
    return '';
  },
  axios: libraryAxios,
  loading: false,
});

export const AuthContextProvider: FC = ({ children }) => {
  const [mode, setMode] = useState<LoginModes>();

  const [sorensonUser, setSorensonUser] = useState<ISorensonUser>(
    getLocalValue<ISorensonUser>('sorenson_user') || sorensonUserInitial
  );
  if (process.env.REACT_APP_CORE_REQUEST_ENDPOINT)
    Core.updateConfig({
      coreRequestUrl: process.env.REACT_APP_CORE_REQUEST_ENDPOINT,
    });
  const [userZoomData, setUserZoomData] = useState<IUserZoomData>(zoomInitial);
  const [accessToken, setAccessToken] = useState<string>('');
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    (getLocalValue<boolean>('is_logged_in') as boolean) || false
  );
  const [loading, setLoading] = useState(true);
  const [ip, setIp] = useState<string>('');
  const [location, setLocation] = useState<string | null>(
    getLocalValue<string>(LOCATION)
  );

  const axios = useAxios(accessToken, setAccessToken);

  const {
    loginWithRedirect,
    user: auth0User,
    isAuthenticated,
    isLoading,
    error,
    getAccessTokenSilently,
    logout,
  } = useAuth0();

  const getUserZoomData = useCallback(async () => {
    try {
      const { data } = await axios.get('me');

      const { data: hearingPhone } = await axios.get(
        `hearingNumber/${data.type === ZoomType.Pro}`
      );
      const user = {
        ...data,
        hearingPhoneNumber: hearingPhone.phoneNumber,
        canRequest: true,
        accountId: data.account_id,
        firstName: data.first_name,
        lastName: data.last_name,
      };
      setUserZoomData(user);
    } catch (error: any) {
      console.error(error);
    }
  }, [axios]);

  const setUserData = (
    name: string,
    phoneNumber: number,
    userType: UserType
  ) => {
    setSorensonUser({ name, phoneNumber, userType });
    getUserZoomData();
    setLocalItem(
      'sorenson_user',
      JSON.stringify({ name, phoneNumber, userType })
    );
  };

  const clearUserData = () => {
    setSorensonUser(sorensonUserInitial);
    setLocation('');
    removeLocalItem(LOCATION);
    removeLocalItem('sorenson_user');
    removeLocalItem('is_logged_in');
    setUserZoomData(zoomInitial);
  };

  const signIn = async ({ username, password }: LogInCredentials) => {
    setMode(LoginModes.Sorenson);
    const provider = new SorensonAccountProvider();
    Core.updateConfig({
      coreRequestUrl: process.env.REACT_APP_CORE_REQUEST_URL,
    });
    try {
      const { loginStatus, statusMessage } = await provider.userLoginCheck({
        username,
        password,
      });
      if (loginStatus !== UserLoginStatus.notValid) {
        setUserData(username, +username, UserType.VRS);
        setIsLoggedIn(true);
        setLocalItem<boolean>('is_logged_in', true);
      } else {
        throw new Error(statusMessage);
      }
    } catch (e) {
      console.log('There was an error while logging in', e);
      throw e;
    }
  };

  const signOut = () => {
    if (mode === LoginModes.Auth0) {
      clearUserData();
      setIsLoggedIn(false);
      logout({ returnTo: `${window.location.origin}/login` });
    } else {
      clearUserData();
      setIsLoggedIn(false);
    }
  };

  const auth0Login = () => {
    loginWithRedirect();
  };

  const getTokenSilently = async () => {
    return mode === LoginModes.Auth0 ? await getAccessTokenSilently() : '';
  };

  const getIp = async () => {
    try {
      const userPublicIp = await publicIpv4();
      setIp(userPublicIp);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!ip) {
      getIp();
    }
  }, [ip]);

  useEffect(() => {
    const locationValue = getLocalValue<string>(LOCATION);
    if (locationValue) setLocation(locationValue);
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      setMode(LoginModes.Auth0);
      setIsLoggedIn(true);
    }
  }, [error, isAuthenticated, isLoading]);

  useEffect(() => {
    if (auth0User) {
      setSorensonUser({
        name: auth0User.name || '',
        phoneNumber: undefined,
        email: auth0User.email || '',
        userType: UserType.VRI,
      });
    }
  }, [auth0User]);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  return (
    <AuthContext.Provider
      value={{
        axios,
        sorensonUser,
        userZoomData,
        accessToken,
        isLoggedIn,
        ip,
        location,
        loading,
        setAccessToken,
        setUserData,
        clearUserData,
        signIn,
        signOut,
        auth0Login,
        getUserZoomData,
        setLocation,
        getTokenSilently,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
